import type { ApiClient } from "@pokifolio/api-client";
import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  type RouterHistory,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";

import { ChangePasswordPage } from "../features/auth/change-password-page";
import { LoginPage } from "../features/auth/login-page";
import { RequirePermission } from "../features/auth/require-permission";
import { meQueryOptions } from "../features/auth/session";
import { CardPage } from "../features/cards/card-page";
import { CardsPage } from "../features/cards/cards-page";
import { parseListingSearch } from "../features/cards/listing-filters";
import { ListingsPage } from "../features/cards/listings-page";
import { DashboardPage } from "../features/dashboard/dashboard-page";
import { PortfolioItemPage } from "../features/portfolio/portfolio-item-page";
import { PortfolioListPage } from "../features/portfolio/portfolio-list-page";
import { PortfolioNewPage } from "../features/portfolio/portfolio-new-page";
import { ApprovalsPage } from "../features/research/approvals-page";
import { parseResearchListSearch } from "../features/research/list-search";
import { ResearchDetailPage } from "../features/research/research-detail-page";
import { ResearchListPage } from "../features/research/research-list-page";
import { SearchPage } from "../features/search/search-page";
import { UsersPage } from "../features/users/users-page";
import { AppShell } from "./app-shell";
import { ErrorPage, NotFoundPage } from "./status-pages";

export interface RouterContext {
  readonly api: ApiClient;
  readonly queryClient: QueryClient;
}

const loadMe = (context: RouterContext) => context.queryClient.query(meQueryOptions(context.api));

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  beforeLoad: async ({ context }) => {
    if ((await loadMe(context)) !== null) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/change-password",
  beforeLoad: async ({ context }) => {
    if ((await loadMe(context)) === null) throw redirect({ to: "/login" });
  },
  component: ChangePasswordPage,
});

/** Every screen below requires a signed-in user who has set their own password. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context }) => {
    const me = await loadMe(context);
    if (me === null) throw redirect({ to: "/login" });
    if (me.user.mustChangePassword) throw redirect({ to: "/change-password" });
  },
  component: AppShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: DashboardPage,
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: () => (
    <RequirePermission permission="users.manage">
      <UsersPage />
    </RequirePermission>
  ),
});

const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/search",
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === "string" && search.q.trim() !== "" ? { q: search.q.trim().slice(0, 200) } : {},
  component: function SearchRoute() {
    const { q } = searchRoute.useSearch();
    return (
      <RequirePermission permission="search.run">
        <SearchPage key={q} initialWords={q} />
      </RequirePermission>
    );
  },
});

const researchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/research",
  validateSearch: parseResearchListSearch,
  component: function ResearchListRoute() {
    // Parsed again: parent routes pass every address value through, including ones this page rejected.
    const filters = parseResearchListSearch(researchRoute.useSearch());
    const navigate = researchRoute.useNavigate();
    return (
      <RequirePermission permission="research.create">
        <ResearchListPage filters={filters} onFiltersChange={(next) => void navigate({ search: next })} />
      </RequirePermission>
    );
  },
});

const researchDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/research/$researchId",
  component: function ResearchDetailRoute() {
    const { researchId } = researchDetailRoute.useParams();
    return (
      <RequirePermission permission="research.create">
        <ResearchDetailPage researchId={researchId} />
      </RequirePermission>
    );
  },
});

const cardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cards",
  component: () => (
    <RequirePermission permission="research.create">
      <CardsPage />
    </RequirePermission>
  ),
});

const cardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cards/$cardId",
  component: function CardRoute() {
    const { cardId } = cardRoute.useParams();
    return (
      <RequirePermission permission="research.create">
        <CardPage key={cardId} cardId={cardId} />
      </RequirePermission>
    );
  },
});

const listingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/listings",
  validateSearch: parseListingSearch,
  component: function ListingsRoute() {
    const filters = parseListingSearch(listingsRoute.useSearch());
    const navigate = listingsRoute.useNavigate();
    return (
      <RequirePermission permission="research.create">
        <ListingsPage filters={filters} onFiltersChange={(next) => void navigate({ search: next })} />
      </RequirePermission>
    );
  },
});

const approvalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/approvals",
  component: () => (
    <RequirePermission permission="research.approve">
      <ApprovalsPage />
    </RequirePermission>
  ),
});

const portfolioRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/portfolio",
  component: () => (
    <RequirePermission permission="holdings.view">
      <PortfolioListPage />
    </RequirePermission>
  ),
});

const portfolioNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/portfolio/new",
  validateSearch: (search: Record<string, unknown>): { research?: string } =>
    typeof search.research === "string" && /^[0-9a-f-]{36}$/i.test(search.research)
      ? { research: search.research }
      : {},
  component: function PortfolioNewRoute() {
    const { research } = portfolioNewRoute.useSearch();
    return (
      <RequirePermission permission="holdings.update">
        <PortfolioNewPage researchId={research} />
      </RequirePermission>
    );
  },
});

const portfolioItemRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/portfolio/$itemId",
  component: function PortfolioItemRoute() {
    const { itemId } = portfolioItemRoute.useParams();
    return (
      <RequirePermission permission="holdings.view">
        <PortfolioItemPage itemId={itemId} />
      </RequirePermission>
    );
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  changePasswordRoute,
  appRoute.addChildren([
    dashboardRoute,
    searchRoute,
    researchRoute,
    researchDetailRoute,
    cardsRoute,
    cardRoute,
    listingsRoute,
    approvalsRoute,
    portfolioRoute,
    portfolioNewRoute,
    portfolioItemRoute,
    usersRoute,
  ]),
]);

export function createAppRouter(context: RouterContext, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context,
    ...(history === undefined ? {} : { history }),
    defaultErrorComponent: ({ error }) => <ErrorPage error={error} />,
    defaultPendingMinMs: 0,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
